import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Banner,
  ButtonRow,
  DateInput,
  DecisionPanel,
  NoteBox,
  PanelLabel,
  SignUpButton,
  SmallPrint,
  TurnDownButton,
} from "../BoardAppealsStyled";

/**
 * Sign up or turn down. A turn-down needs a reason; a sign-up does not.
 * Whoever sent it (or Alexa, for an intake that came from Litify) is told.
 * The veteran is never emailed from here.
 */
const AppealDecision = ({ card, busy, error, onDecide }) => {
  const [note, setNote] = useState("");
  const [boardDate, setBoardDate] = useState(card.boardDate || "");
  const [needNote, setNeedNote] = useState(false);
  const noteRef = useRef(null);

  useEffect(() => {
    setNote("");
    setBoardDate(card.boardDate || "");
    setNeedNote(false);
  }, [card.id, card.boardDate]);

  if (!card.canDecide) {
    return (
      <DecisionPanel>
        <PanelLabel as="div">
          {card.statusLabel}
          {card.decidedBy ? ` by ${card.decidedBy.name}` : ""}
        </PanelLabel>
        {card.decisionNote ? <div>{card.decisionNote}</div> : null}
        {card.litifyResult ? <SmallPrint>{card.litifyResult}</SmallPrint> : null}
      </DecisionPanel>
    );
  }

  const tellWho = card.sentBy?.name || "Alexa";

  const submit = (action) => {
    if (action === "turnDown" && !note.trim()) {
      setNeedNote(true);
      noteRef.current?.focus();
      return;
    }
    onDecide(action, note.trim(), boardDate || null);
  };

  return (
    <DecisionPanel>
      <PanelLabel htmlFor="board-appeal-note">Note to {tellWho}</PanelLabel>
      <NoteBox
        id="board-appeal-note"
        ref={noteRef}
        value={note}
        $invalid={needNote}
        disabled={busy}
        placeholder="Why — needed to turn it down"
        onChange={(e) => {
          setNote(e.target.value);
          if (needNote && e.target.value.trim()) setNeedNote(false);
        }}
      />
      {needNote ? (
        <Banner role="alert" style={{ margin: 0 }}>
          Write why you are turning this down first.
        </Banner>
      ) : null}
      <PanelLabel htmlFor="board-appeal-date">Board decision date</PanelLabel>
      <DateInput
        id="board-appeal-date"
        type="date"
        value={boardDate}
        disabled={busy}
        onChange={(e) => setBoardDate(e.target.value)}
      />
      {error ? (
        <Banner role="alert" style={{ margin: 0 }}>
          {error}
        </Banner>
      ) : null}
      <ButtonRow>
        <SignUpButton type="button" disabled={busy} onClick={() => submit("signUp")}>
          {busy === "signUp" ? "Signing up…" : "Sign up"}
        </SignUpButton>
        <TurnDownButton
          type="button"
          disabled={busy}
          onClick={() => submit("turnDown")}
        >
          {busy === "turnDown" ? "Turning down…" : "Turn down"}
        </TurnDownButton>
      </ButtonRow>
      <SmallPrint>
        {card.litifyWritesOn
          ? `${tellWho} is emailed. Litify is updated and the Notice of Appeal deadline goes on the calendar.`
          : `${tellWho} is emailed. Litify is not changed from here yet.`}
      </SmallPrint>
    </DecisionPanel>
  );
};

AppealDecision.propTypes = {
  card: PropTypes.object.isRequired,
  busy: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  error: PropTypes.string,
  onDecide: PropTypes.func.isRequired,
};

export default AppealDecision;
